import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Cloud, CloudRain, CloudLightning, Sun, CloudSun, Wind, Droplets,
  AlertTriangle, RefreshCw, MapPin, Thermometer, X, Bell, BellOff,
  CloudSnow, CloudFog, Loader2
} from "lucide-react";

// Pre-generate rain drop positions
const generateRainDrops = (count) => {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    left: `${(i * 2) % 100}%`,
    animationDelay: `${(i * 0.04) % 2}s`,
    animationDuration: `${0.5 + (i * 0.01) % 0.5}s`
  }));
};

const RAIN_DROPS_ALERT = generateRainDrops(50);
const RAIN_DROPS_WIDGET = generateRainDrops(20);

// Weather icon component based on condition
const WeatherIcon = ({ condition, code, size = 48 }) => {
  const iconClass = `w-${size/4} h-${size/4}`;
  
  // Thunderstorm
  if (code >= 200 && code < 300) {
    return <CloudLightning className={`${iconClass} text-yellow-400 drop-shadow-[0_0_15px_rgba(250,204,21,0.8)]`} />;
  }
  // Drizzle or Rain
  if ((code >= 300 && code < 400) || (code >= 500 && code < 600)) {
    return <CloudRain className={`${iconClass} text-neon-blue drop-shadow-[0_0_15px_rgba(0,243,255,0.8)]`} />;
  }
  // Snow
  if (code >= 600 && code < 700) {
    return <CloudSnow className={`${iconClass} text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.8)]`} />;
  }
  // Atmosphere (fog, mist, etc)
  if (code >= 700 && code < 800) {
    return <CloudFog className={`${iconClass} text-gray-400`} />;
  }
  // Clear
  if (code === 800) {
    return <Sun className={`${iconClass} text-yellow-400 drop-shadow-[0_0_15px_rgba(250,204,21,0.8)]`} />;
  }
  // Clouds
  if (code > 800) {
    return <CloudSun className={`${iconClass} text-gray-300`} />;
  }
  
  return <Cloud className={`${iconClass} text-gray-400`} />;
};

// Rain alert animation component
const RainAlert = ({ onClose, onCancelSessions }) => {
  // Direct handler for dismiss that ensures localStorage is set
  const handleDismiss = () => {
    localStorage.setItem('rainAlertDismissed', Date.now().toString());
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
      {/* Animated rain drops background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {RAIN_DROPS_ALERT.map((drop) => (
          <div
            key={drop.id}
            className="absolute w-0.5 h-8 bg-gradient-to-b from-neon-blue to-transparent rounded-full animate-rain"
            style={{
              left: drop.left,
              animationDelay: drop.animationDelay,
              animationDuration: drop.animationDuration
            }}
          />
        ))}
      </div>
      
      <div className="relative max-w-md w-full">
        {/* Glowing border */}
        <div className="absolute -inset-1 bg-gradient-to-r from-neon-blue via-red-500 to-neon-pink rounded-2xl blur-lg opacity-75 animate-pulse" />
        
        <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-xl border-2 border-red-500/50 p-6 text-center">
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-gray-500 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Alert icon */}
          <div className="w-24 h-24 mx-auto mb-4 relative">
            <div className="absolute inset-0 bg-red-500/20 rounded-full animate-ping" />
            <div className="relative w-full h-full bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center">
              <CloudRain className="w-12 h-12 text-white" />
            </div>
          </div>
          
          <h2 className="font-graffiti text-3xl text-red-500 mb-2">RAIN ALERT!</h2>
          <p className="font-orbitron text-sm text-gray-300 mb-4">
            Weather conditions may affect practice sessions
          </p>
          
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
            <p className="font-rajdhani text-sm text-red-400">
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              Rain or storm detected in your area. Consider cancelling outdoor practice sessions.
            </p>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={handleDismiss}
              data-testid="rain-alert-dismiss"
              className="flex-1 px-4 py-3 border border-white/20 text-gray-400 font-orbitron text-sm hover:bg-white/5 transition-colors rounded-lg"
            >
              DISMISS
            </button>
            <button
              onClick={onCancelSessions}
              className="flex-1 px-4 py-3 bg-red-500 text-white font-orbitron text-sm hover:bg-red-600 transition-colors rounded-lg"
              data-testid="cancel-sessions-btn"
            >
              CANCEL PRACTICE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const WeatherTracker = ({ compact = false }) => {
  const { user, authAxios } = useAuth();
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRainAlert, setShowRainAlert] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const isAdmin = user?.role === 'admin';

  // Check localStorage immediately on component initialization
  const [alertDismissed, setAlertDismissed] = useState(() => {
    try {
      const dismissed = localStorage.getItem('rainAlertDismissed');
      if (!dismissed) return false;
      const dismissedTime = parseInt(dismissed);
      if (isNaN(dismissedTime)) return false;
      const now = Date.now();
      // Check if dismissed within the last 24 hours
      return (now - dismissedTime) < 24 * 60 * 60 * 1000;
    } catch (e) {
      return false;
    }
  });

  // Mark alert as dismissed
  const dismissRainAlert = useCallback(() => {
    try {
      localStorage.setItem('rainAlertDismissed', Date.now().toString());
      setAlertDismissed(true);
    } catch (e) {
      console.error('Failed to save dismissal to localStorage', e);
    }
    setShowRainAlert(false);
  }, []);

  // Get user's location using geolocation API
  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          // Check if user is in Florida (roughly lat 24.5-31, lon -87.6 to -80)
          const isInFlorida = latitude >= 24.5 && latitude <= 31 && longitude >= -87.6 && longitude <= -80;
          if (isInFlorida) {
            setUserLocation({ lat: latitude, lon: longitude });
          }
        },
        (error) => {
          console.log("Geolocation not available or denied, using default Palm Beach location");
        },
        { timeout: 5000, enableHighAccuracy: false }
      );
    }
  }, []);

  const fetchWeather = useCallback(async () => {
    try {
      // Build URL with user's coordinates if available
      let url = `${API}/weather`;
      if (userLocation) {
        url += `?lat=${userLocation.lat}&lon=${userLocation.lon}`;
      }
      
      const response = await axios.get(url);
      const data = response.data;
      setWeather(data);
      
      // DISABLED: Automatic rain alert popup
      // The modal was causing UX issues by blocking the page on every visit
      // Users can still see rain status in the weather widget and navbar
      // To re-enable: uncomment the following code
      /*
      if (!compact && data.is_raining && alertsEnabled && !alertDismissed) {
        setShowRainAlert(true);
      }
      */
    } catch (e) {
      console.error("Failed to fetch weather:", e);
    } finally {
      setLoading(false);
    }
  }, [alertsEnabled, userLocation, compact, alertDismissed]);

  useEffect(() => {
    fetchWeather();
    
    // Refresh weather every 10 minutes
    const interval = setInterval(fetchWeather, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchWeather]);

  const handleCancelSessions = async () => {
    if (!isAdmin) {
      toast.error("Only admins can cancel sessions");
      return;
    }
    
    try {
      const response = await authAxios.post('/weather/cancel-sessions');
      toast.success(response.data.message);
      dismissRainAlert();
    } catch (e) {
      toast.error("Failed to cancel sessions");
    }
  };

  if (loading) {
    return (
      <div className={`${compact ? 'p-3' : 'p-6'} flex items-center justify-center`}>
        <Loader2 className="w-6 h-6 text-neon-blue animate-spin" />
      </div>
    );
  }

  if (!weather) return null;

  // Compact version for header/nav - no modal here, only in full version
  if (compact) {
    return (
      <div 
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
          weather.is_raining 
            ? 'bg-red-500/10 border-red-500/30 animate-pulse' 
            : 'bg-white/5 border-white/10 hover:border-neon-blue/30'
        }`}
        data-testid="weather-compact"
        title={weather.is_raining ? "Rain detected - scroll to weather section for details" : weather.condition}
      >
        <WeatherIcon condition={weather.condition} code={weather.weather_code} size={24} />
        <span className="font-orbitron text-sm text-white">{weather.temperature}°</span>
        {weather.is_raining && (
          <AlertTriangle className="w-4 h-4 text-red-500 animate-pulse" />
        )}
      </div>
    );
  }

  // Full weather widget
  return (
    <>
      <div 
        className="relative overflow-hidden rounded-xl"
        data-testid="weather-tracker"
      >
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className={`absolute inset-0 ${
            weather.is_raining 
              ? 'bg-gradient-to-br from-gray-900 via-blue-900/30 to-gray-900' 
              : 'bg-gradient-to-br from-gray-900 via-neon-blue/5 to-gray-900'
          }`} />
          {weather.is_raining && (
            <div className="absolute inset-0 overflow-hidden">
              {RAIN_DROPS_WIDGET.map((drop) => (
                <div
                  key={drop.id}
                  className="absolute w-0.5 h-6 bg-gradient-to-b from-neon-blue/50 to-transparent rounded-full animate-rain"
                  style={{
                    left: drop.left,
                    animationDelay: drop.animationDelay,
                    animationDuration: drop.animationDuration
                  }}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Glowing border for rain alert */}
        {weather.is_raining && (
          <div className="absolute -inset-0.5 bg-gradient-to-r from-neon-blue via-red-500 to-neon-pink rounded-xl blur opacity-50 animate-pulse" />
        )}
        
        <div className="relative border border-white/10 rounded-xl p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-neon-pink" />
              <span className="font-orbitron text-sm text-white">
                {weather.location}{weather.state ? `, ${weather.state}` : ', FL'}
              </span>
              {weather.is_demo && (
                <span className="text-[10px] px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded font-orbitron">DEMO</span>
              )}
              {userLocation && (
                <span className="text-[10px] px-2 py-0.5 bg-neon-green/20 text-neon-green rounded font-orbitron">📍 YOUR AREA</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAlertsEnabled(!alertsEnabled)}
                className={`p-2 rounded-lg transition-colors ${
                  alertsEnabled ? 'bg-neon-pink/20 text-neon-pink' : 'bg-white/5 text-gray-500'
                }`}
                title={alertsEnabled ? "Alerts enabled" : "Alerts disabled"}
              >
                {alertsEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
              </button>
              <button
                onClick={fetchWeather}
                className="p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                title="Refresh weather"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Main weather display */}
          <div className="flex items-center gap-6 mb-6">
            <div className="relative">
              {/* Glow effect behind icon */}
              <div className={`absolute inset-0 rounded-full blur-xl ${
                weather.is_raining ? 'bg-neon-blue/30' : 'bg-yellow-400/20'
              }`} />
              <WeatherIcon condition={weather.condition} code={weather.weather_code} size={80} />
            </div>
            
            <div>
              <div className="flex items-baseline gap-2">
                <span className="font-orbitron text-5xl text-white">{weather.temperature}</span>
                <span className="font-orbitron text-2xl text-gray-500">°F</span>
              </div>
              <p className="font-rajdhani text-lg text-gray-400 capitalize">{weather.description}</p>
            </div>
          </div>
          
          {/* Weather stats */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <Thermometer className="w-5 h-5 text-neon-pink mx-auto mb-1" />
              <p className="font-orbitron text-xs text-gray-500">FEELS LIKE</p>
              <p className="font-orbitron text-lg text-white">{weather.feels_like}°</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <Droplets className="w-5 h-5 text-neon-blue mx-auto mb-1" />
              <p className="font-orbitron text-xs text-gray-500">HUMIDITY</p>
              <p className="font-orbitron text-lg text-white">{weather.humidity}%</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <Wind className="w-5 h-5 text-gray-400 mx-auto mb-1" />
              <p className="font-orbitron text-xs text-gray-500">WIND</p>
              <p className="font-orbitron text-lg text-white">{weather.wind_speed} mph</p>
            </div>
          </div>
          
          {/* Rain/Storm Alert Banner */}
          {weather.is_raining && (
            <div 
              className="bg-gradient-to-r from-red-500/20 via-orange-500/20 to-red-500/20 border border-red-500/30 rounded-lg p-4 cursor-pointer hover:bg-red-500/30 transition-colors"
              onClick={() => setShowRainAlert(true)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center animate-pulse">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <div className="flex-1">
                  <p className="font-orbitron text-sm text-red-400">WEATHER ALERT</p>
                  <p className="font-rajdhani text-xs text-gray-400">{weather.alert.message}</p>
                </div>
                <span className="font-orbitron text-xs text-red-400 animate-pulse">TAP FOR ACTION</span>
              </div>
            </div>
          )}
          
          {/* Last updated */}
          <p className="text-center mt-4 font-rajdhani text-xs text-gray-600">
            Last updated: {new Date(weather.updated_at).toLocaleTimeString()}
          </p>
        </div>
      </div>
      
      {showRainAlert && (
        <RainAlert 
          onClose={dismissRainAlert}
          onCancelSessions={handleCancelSessions}
        />
      )}
      
      {/* Rain animation CSS */}
      <style>{`
        @keyframes rain {
          0% {
            transform: translateY(-100%);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translateY(100vh);
            opacity: 0;
          }
        }
        .animate-rain {
          animation: rain linear infinite;
        }
      `}</style>
    </>
  );
};

export default WeatherTracker;
